import { unwrapElementData } from '@/lib/checkpoint/checkpoint-to-shapes'
import { type AnyRecord, asRecord, pick, pickArray, pickRecord } from './tolerant-read'
import type { AttrSpec } from '../types/attr-spec'
import type { XmlNode } from '../types/xml-node'
import { buildAttrs, genericNode, node } from './xml-writer'
import {
  CURVE_ATTRS,
  DS_ATTRS,
  FS_ATTRS,
  GR_ATTRS,
  IM_ATTRS,
  LS_ATTRS,
  MOVE_LINE_ATTRS,
  PE_ATTRS,
  PG_ATTRS,
  PX_ATTRS,
  SFI_ATTRS,
  TL_ATTRS,
  TOP_ATTRS,
  TR_ATTRS,
  WP_ATTRS,
} from '../constants/nsd-attr-specs'

/* ------------------------------------------------------------------ *
 * Element content builders
 * ------------------------------------------------------------------ */

/** Emits an optional child element only when the source object is actually on the wire. */
export function optional(
  source: AnyRecord | null,
  tag: string,
  specs: readonly AttrSpec[],
  children: XmlNode[] = []
): XmlNode[] {
  if (!source) return []

  return [node(tag, buildAttrs(source, specs), children)]
}

export function buildFillStyle(source: AnyRecord | null): XmlNode[] {
  if (!source) return []

  const gradient = pickRecord(source, 'gradient')

  return [node('FS', buildAttrs(source, FS_ATTRS), optional(gradient, 'GR', GR_ATTRS))]
}

export function buildTextRun(raw: unknown): XmlNode | null {
  const run = asRecord(raw)

  if (!run) return null

  return node('TR', buildAttrs(run, TR_ATTRS), [
    ...optional(pickRecord(run, 'lineStyle'), 'LS', LS_ATTRS),
    ...buildFillStyle(pickRecord(run, 'fillStyle')),
  ])
}

export function buildTextLine(raw: unknown): XmlNode | null {
  const line = asRecord(raw)

  if (!line) return null

  const runs = pickArray(line, 'textRuns')
    .map(buildTextRun)
    .filter((n): n is XmlNode => n !== null)

  return node('TL', buildAttrs(line, TL_ATTRS), runs)
}

export function buildParagraph(raw: unknown): XmlNode | null {
  const paragraph = asRecord(raw)

  if (!paragraph) return null

  const lines = pickArray(paragraph, 'textLines')
    .map(buildTextLine)
    .filter((n): n is XmlNode => n !== null)

  return node('PG', buildAttrs(paragraph, PG_ATTRS), lines)
}

export function buildTextContent(source: AnyRecord): XmlNode {
  const paragraphs = pickArray(source, 'paragraphs')
    .map(buildParagraph)
    .filter((n): n is XmlNode => n !== null)

  return node('TX', [], paragraphs)
}

/** `Content` is a protobuf `oneof`, so exactly one of TX / IM is present. */
export function buildContentContainer(source: AnyRecord | null): XmlNode[] {
  if (!source) return []

  const text = pickRecord(source, 'text')

  if (text) return [node('XX', [], [buildTextContent(text)])]

  const image = pickRecord(source, 'image')

  if (image) return [node('XX', [], [node('IM', buildAttrs(image, IM_ATTRS))])]

  return []
}

export function buildPathNode(source: AnyRecord | null): XmlNode[] {
  if (!source) return []

  const children: XmlNode[] = []

  // Command order is load-bearing - a path is one or more MT-started contours.
  for (const raw of pickArray(source, 'commands')) {
    const command = asRecord(raw)

    if (!command) continue

    const moveTo = pickRecord(command, 'moveTo')

    if (moveTo) {
      children.push(node('MT', buildAttrs(moveTo, MOVE_LINE_ATTRS)))
      continue
    }

    const lineTo = pickRecord(command, 'lineTo')

    if (lineTo) {
      children.push(node('LT', buildAttrs(lineTo, MOVE_LINE_ATTRS)))
      continue
    }

    const curveTo = pickRecord(command, 'curveTo')

    if (curveTo) children.push(node('CT', buildAttrs(curveTo, CURVE_ATTRS)))
  }

  return [node('P', [], children)]
}

export function shapeCommonChildren(decoded: AnyRecord): {
  lineStyle: XmlNode[]
  fillStyle: XmlNode[]
  content: XmlNode[]
  shapeFill: XmlNode[]
  path: XmlNode[]
} {
  return {
    lineStyle: optional(pickRecord(decoded, 'lineStyle'), 'LS', LS_ATTRS),
    fillStyle: buildFillStyle(pickRecord(decoded, 'fillStyle')),
    content: buildContentContainer(pickRecord(decoded, 'content')),
    shapeFill: optional(pickRecord(decoded, 'shapeImageFill'), 'SFI', SFI_ATTRS),
    path: buildPathNode(pickRecord(decoded, 'path')),
  }
}

/**
 * Child order follows the Go content structs (`XmlRectangleContent` and friends).
 * Returns null when the kindId has no spec, so the caller can fall back to `genericNode`.
 */
export function buildShapeContent(kindId: number, decoded: AnyRecord): XmlNode | null {
  const { lineStyle, fillStyle, content, shapeFill, path } = shapeCommonChildren(decoded)

  switch (kindId) {
    case 1:
      return node(
        'RR',
        buildAttrs(decoded, [{ attr: 'RC', from: 'roundedCorner', kind: 'decimal', def: '0' }]),
        [...lineStyle, ...fillStyle, ...content, ...shapeFill]
      )
    case 2:
      return node('IP', [], [...path, ...lineStyle, ...fillStyle, ...content, ...shapeFill])
    case 9:
      return node('PL', [], [...path, ...lineStyle, ...content, ...shapeFill])
    case 10:
      return node(
        'RP',
        buildAttrs(decoded, [
          { attr: 'S', from: 'sides', kind: 'uint' },
          { attr: 'I', from: 'inner', kind: 'string' },
        ]),
        [...fillStyle, ...content, ...shapeFill]
      )
    case 5: {
      const runs = pickArray(decoded, 'textRuns')
        .map(buildTextRun)
        .filter((n): n is XmlNode => n !== null)

      return node(
        'PN',
        buildAttrs(decoded, [{ attr: 'LR', from: 'leftRight', kind: 'string' }]),
        runs
      )
    }

    case 6: {
      const characters = pickArray(decoded, 'characters')
        .map(asRecord)
        .filter((c): c is AnyRecord => c !== null)
        .map((c) => node('PX', buildAttrs(c, PX_ATTRS)))

      return node('TOP', buildAttrs(decoded, TOP_ATTRS), characters)
    }

    // Oval is not created by the current editor, but `STOWED_ELEMENT_FIELDS` still reads kindId 8,
    // and `XmlOvalContent` is a three-child struct - cheaper to map than to explain in a comment.
    case 8:
      return node('OV', [], [...lineStyle, ...fillStyle, ...content])

    // kindId 4 is legacy-read-only: a bare `<PE><IM>` with no wrapping shape. Normalized away on
    // load, but a mid-deploy save can still carry it, so it round-trips here rather than falling
    // through to the generic emitter.
    case 4:
      return node('IM', buildAttrs(decoded, IM_ATTRS))
    default:
      return null
  }
}

/* ------------------------------------------------------------------ *
 * Elements
 * ------------------------------------------------------------------ */

export const GROUP_KIND = 7

/**
 * Group children live on the StowedElement wrapper, falling back to the decoded body - the same
 * two-place lookup `layerChildElements` in `checkpoint-to-shapes.ts` performs.
 */
export function groupChildren(element: AnyRecord, decoded: AnyRecord): unknown[] {
  const fromWrapper = pickArray(element, 'elements')

  if (fromWrapper.length > 0) return fromWrapper

  return pickArray(decoded, 'elements')
}

export function buildElementNode(raw: unknown): XmlNode | null {
  const element = asRecord(raw)

  if (!element) return null

  const objectId = String(pick(element, 'objectId') ?? 'unknown')
  const kindIdRaw = pick(element, 'kindId')
  const kindId = Number(kindIdRaw)
  const rawDecoded = pickRecord(element, 'decodedData')

  // `getSpreadCheckpoint` accepts `decodeElements: false` and `decodeElementsLimit`, so an element
  // can legitimately arrive with no body. Say so rather than emitting a hollow PE.
  if (!rawDecoded) {
    return { tag: '', attrs: [], children: [], comment: `element ${objectId}: not decoded` }
  }

  if (!Number.isFinite(kindId)) {
    return genericNode('PE', rawDecoded, `element ${objectId}: unknown kindId ${String(kindIdRaw)}`)
  }

  // `base` may sit under a kind-specific field rather than top-level; reuse the loader's unwrap so
  // the XML sees exactly what the canvas sees.
  const decoded = unwrapElementData(rawDecoded, kindId)
  const base = pickRecord(decoded, 'base')
  const attrs = buildAttrs(base, PE_ATTRS)
  const textWrap = optional(pickRecord(decoded, 'textWrap'), 'WP', WP_ATTRS)

  if (kindId === GROUP_KIND) {
    const children = groupChildren(element, decoded)
      .map(buildElementNode)
      .filter((n): n is XmlNode => n !== null)

    return node('GP', attrs, [...children, ...textWrap])
  }

  const content = buildShapeContent(kindId, decoded)
  const dropShadow = optional(pickRecord(decoded, 'dropShadow'), 'DS', DS_ATTRS)

  if (!content) {
    const fallback = genericNode(
      'PE',
      decoded,
      `kindId ${kindId} has no spec (element ${objectId})`
    )

    return fallback
  }

  return node('PE', attrs, [content, ...dropShadow, ...textWrap])
}
